import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import PublicPage from "components/PublicPage";
import React from "react";
import { EMAILS } from "utils/constants";

export default function FaqPage() {
  return (
    <PublicPage
      metadata={{
        title: "Frequently Asked Questions | Pipers For Hire",
        description:
          "Read answers to a collection of the most frequently asked questions to help you in booking a bagpiper.",
        url: "https://pipersforhire.com/faqs",
      }}
    >
      <section>
        <h1 className="text-2xl font-bold text-theme-blue">
          Frequently Asked Questions
        </h1>
        <p className="mt-3">
          Many of our most commonly asked questions are answered below. If you
          are a bagpiper, please refer to the section for talent.
        </p>
      </section>

      <section id="client" className="mt-32">
        <h2 className="text-2xl font-bold text-theme-blue">Client FAQs</h2>
        <FaqItem
          question="Why can't I find bagpipers in my area?"
          answer={`Depending on the location, we may have not yet found an available, local bagpiper. We are actively expanding our network of bagpipers, but you can help us prioritize your area by emailing us at ${EMAILS.CONTACT}.`}
        />
        <FaqItem
          question="What if my event date or location changes?"
          answer={`Any changes should be discussed with the bagpiper as soon as they are known to ensure they are still available. Ideally that will continue to be available and no other changes are needed. If they are no longer available, or if deposit refunds are needed, or if the balance due date has changed, please reach out to our team at ${EMAILS.CONTACT} so we can make adjustments through the billing system.`}
        />
        <FaqItem
          question="How do I pay the piper?"
          answer="Pipers For Hire uses an industry-leading provider to generate invoices and securely process payments online. Payment through the platform is both convenient and is backed by our Worry-Free Guarantee, meaning that a full refund will be covered if there is a last-minute cancellation or a no-show."
        />
      </section>

      <section id="bagpiper" className="mt-16">
        <h2 className="text-2xl font-bold text-theme-blue">Bagpiper FAQs</h2>
        <h3 className="mt-11 text-xl font-bold text-theme-dark-blue">
          Joining and membership:
        </h3>
        <FaqItem
          question="Can I join?"
          answer="Yes, as long as you are located within the U.S. you are eligible to join. You can create a free account or explore our membership plans for added features and better commission fees."
        />
        <FaqItem
          question="
            I'm located outside of the U.S., can I join?"
          answer="We are actively working on expanding to include bagpipers all over the world, but for now we only service the U.S. If you're an international bagpiper looking to get listed, please contact us to indicate your interest!"
        />
        <FaqItem
          question="What is the pricing and features of a membership?"
          answer="We currently offer a Pro plan for $8.25/mo. paid annually. The Pro plan allows you to include additional photos, audio recordings, and videos to your profile, adds a badge to your profile listing, and impacts your ranking in search results."
        />
        <h3 className="mt-11 text-xl font-bold text-theme-dark-blue">
          General questions
        </h3>
        <FaqItem
          question="How do the reviews/star ratings work?"
          answer="Reviews can be submitted by anyone at any time through the personal profile pages. Once submitted, they are logged in an internal dashboard for authenticity validation. After they have been authenticated they can be set live and will appear on the member's individual profile page within minutes of approval."
        />
        <FaqItem
          question="How do I improve my ranking on Pipers For Hire?"
          answer="While we do not share details of the algorithm, there are basically two categories of factors: those that can be influenced and those that are determined. Examples of factors that can be influenced include the completeness of a profile and the number and quality of reviews. Because ratings are an important trust signal to clients in the decision-making process -- profiles with performance ratings are generally ranked higher than those without ratings. Members are encouraged to actively reach out to past clients to request reviews. Determined factors include the physical proximity of the bagpiper to the event location."
        />
        <FaqItem
          question="How does Pipers For Hire make money?"
          answer="The primary revenue source is a 5% commission on gigs booked through the platform. This allows bagpipers with a free account to have no upfront costs – the fee is deducted from the total booking amount before being paid out to the bagpiper. Additionally, we also offer annual Pro memberships."
        />
      </section>
    </PublicPage>
  );
}

const FaqItem = (props: { question: string; answer: string }) => (
  <details className="group border-b-2 py-6">
    <summary className="flex cursor-pointer list-none items-center justify-between text-xl font-semibold">
      {props.question}
      <PlusIcon className="h-5 w-5 group-open:hidden" />
      <MinusIcon className="hidden h-5 w-5 group-open:block" />
    </summary>
    <p className="mt-2">{props.answer}</p>
  </details>
);
